// Parse a MultiCash UMSATS movements export (semicolon-delimited). Unlike MT940,
// this carries full SPEI payer details, which we extract: clave de rastreo,
// payer name, ordenante CLABE, payer RFC, and concepto.
const toISO = (ddmmyyyy) => {
  const m = String(ddmmyyyy).match(/(\d{2})\.(\d{2})\.(\d{4})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
};

export function isMulticash(text) {
  const first = String(text).split(/\r?\n/).find((l) => l.trim());
  return !!first && /^\d+;\d+;/.test(first.trim());
}

export function parseMulticash(text) {
  const lines = String(text).split(/\r?\n/).filter((l) => l.trim());
  const movements = [];
  for (const line of lines) {
    const f = line.split(";");
    if (f.length < 14) continue;
    const date = toISO(f[3]);
    const amount = parseFloat(f[10]);
    if (!date || !Number.isFinite(amount)) continue;
    // The SPEI narrative is split into ~27-char chunks across fields 16..end and
    // must be reconstructed by direct concatenation (each chunk keeps its own
    // spaces). Field 5 is the header ("RASTREO <clave>" / "VENTA TPV...") and is
    // taken separately — concatenating it into `detail` glued the clave onto the
    // narrative and corrupted every downstream regex.
    const detail = f.slice(16).join("").replace(/\s+/g, " ").trim();
    const head = (f[5] || "").trim();

    const rfcM = detail.match(/RFC[:\s]+([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{2,3})\b/);
    const clabeM = detail.match(/CLABE[:\s]*(\d{18})/);
    const refM = detail.match(/REFERENCIA[:\s]*(\w+)/);
    const dirM = detail.match(/SPEI\s+(RECIBIDO|ENVIADO)/);
    // Clave de rastreo: from the clean header when it's a SPEI, else from detail.
    const claveRaw = /^RASTREO/i.test(head)
      ? head.replace(/^RASTREO\s+/i, "").trim()
      : (detail.match(/RASTREO\s+([A-Z0-9]+)/) || [])[1] || "";
    // Counterparty ACCOUNT HOLDER — works both ways: "RECIBIDO DE <bank> DE <holder>"
    // and "ENVIADO A <bank> A <holder>". First try skipping the bank segment; fall
    // back to the whole blob. Stop at "(DATO…", "A/DE LA CTA/TARJ", RFC or CLABE.
    let nameM = detail.match(/SPEI\s+(?:RECIBIDO|ENVIADO)\s+(?:DE|A)\s+\d*-?[A-Za-z. ]*?\s+(?:DE|A)\s+(.+?)\s+(?:\(DATO|A LA|DE LA|RFC|CLABE)/);
    if (!nameM) nameM = detail.match(/SPEI\s+(?:RECIBIDO|ENVIADO)\s+(?:DE|A)\s+(.+?)\s+(?:\(DATO|A LA|DE LA|RFC|CLABE)/);
    // Concepto (payer's note for incoming, ours for outgoing e.g. "Devolucion
    // Garantia Reserva 856"); drop the trailing CS/AS settlement code.
    const concM = detail.match(/CONCEPTO[:\s]+(.+?)\s*$/);
    const concepto = concM ? concM[1].replace(/(?:CS|AS)$/, "").trim() : "";

    movements.push({
      date,
      postDate: toISO(f[13]) || date,
      amount: Math.abs(amount),
      credit: amount > 0,
      shortConcept: head,
      reference: (f[6] || refM?.[1] || "").trim(),
      description: detail,
      claveRastreo: claveRaw,
      payerRfc: rfcM && rfcM[1] !== "ND" ? rfcM[1] : null,
      payerClabe: clabeM ? clabeM[1] : null,
      payerName: nameM ? nameM[1].replace(/^\d+-/, "").trim() : "",
      concepto,
      direction: dirM ? dirM[1] : null,
    });
  }
  return { movements };
}
